import { useState } from 'react';
import type { EmailDraft } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';

interface Props {
  draft: EmailDraft;
  /** Öffnet den Antwort-Dialog mit diesem Entwurf. */
  onOpen: (draft: EmailDraft) => void;
  onDiscarded: () => void;
}

/**
 * Ein vorbereiteter, nicht gesendeter Text im Verlauf.
 *
 * Gestrichelter Rahmen und ausgeschriebene Herkunft: ein Entwurf darf keine
 * Sekunde lang wie eine gesendete Mail aussehen. Blasser allein reicht dafür
 * nicht — wer schnell überfliegt, liest Helligkeit als „älter“, nicht als
 * „noch nicht raus“.
 *
 * Stellt nicht nur dar, sondern verwirft auch selbst — `crm.drafts.discard`
 * wird hier aufgerufen, nicht nur gemeldet. Busy/Error-Zustand nach dem
 * Muster von `ConfirmDialog`: ein zweiter Klick vor Antwort des ersten löst
 * nichts aus, und ein Fehlschlag (Netz weg, Entwurf schon gelöscht) zeigt
 * sich statt in einem stillen, wirkungslosen Klick.
 */
export function DraftCard({ draft, onOpen, onDiscarded }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verwerfen = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await crm.drafts.discard(draft.id);
      onDiscarded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwerfen fehlgeschlagen.');
      setBusy(false);
    }
  };

  return (
    <article className="ml-6 rounded-md border border-dashed border-amber-500/60 bg-amber-50/40 p-3 dark:bg-amber-950/20">
      <header className="flex flex-wrap items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-amber-700 dark:text-amber-500">
          Entwurf · {draft.source === 'claude' ? 'von Claude' : 'von dir'} · nicht
          gesendet
        </span>
        <time dateTime={draft.updatedAt}>
          {new Date(draft.updatedAt).toLocaleString('de-DE', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </time>
      </header>

      <p className="mt-2 text-sm font-medium">{draft.subject || '(kein Betreff)'}</p>
      <p className="mt-1 whitespace-pre-wrap text-sm">{draft.body}</p>

      {error && (
        <p className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        {draft.replyToMessageId && (
          <Button variant="outline" size="sm" onClick={() => onOpen(draft)} disabled={busy}>
            Öffnen &amp; Senden
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => void verwerfen()} disabled={busy}>
          {busy ? 'Verwerfe …' : 'Verwerfen'}
        </Button>
      </div>
    </article>
  );
}
